/** The inputs an HTTP agent takes, one row each: the dataset field name, its kind, required. */

import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import type { components } from "@/lib/api-schema";

/** A row being edited; `id` only keeps React's keys stable while names change. */
export type InputRow = Required<components["schemas"]["InputFieldIn"]> & { id: number };

let nextId = 1;
export const newInputRow = (name = ""): InputRow => ({
  id: nextId++,
  name,
  kind: "text",
  required: true,
  description: "",
});

export function InputFieldsEditor({
  rows,
  onChange,
}: {
  rows: InputRow[];
  onChange: (rows: InputRow[]) => void;
}) {
  const update = (id: number, patch: Partial<InputRow>) =>
    onChange(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="pb-1.5 text-[13px] font-semibold">Inputs</legend>
      <p className="-mt-1 text-xs text-muted">
        Each item&apos;s fields with these names are sent in the request body.
      </p>
      {rows.map((r, i) => (
        <div key={r.id} className="flex flex-col gap-1.5 rounded-lg border border-line-soft p-2.5">
          <div className="flex items-center gap-2">
            <input
              aria-label={`Input ${i + 1} name`}
              placeholder="question"
              className="inp flex-1 font-mono"
              value={r.name}
              onChange={(e) => update(r.id, { name: e.target.value })}
            />
            <Select
              label={`Input ${i + 1} kind`}
              className="w-24 shrink-0"
              value={r.kind}
              onChange={(k) => update(r.id, { kind: k as InputRow["kind"] })}
              options={[
                { value: "text", label: "Text" },
                { value: "json", label: "JSON" },
              ]}
            />
            <label className="flex items-center gap-1.5 text-[13px]">
              <input
                type="checkbox"
                className="size-4 accent-ink"
                checked={r.required}
                onChange={(e) => update(r.id, { required: e.target.checked })}
              />
              Required
            </label>
            <Button
              size="sm"
              variant="ghost"
              icon
              aria-label={`Remove input ${i + 1}`}
              onClick={() => onChange(rows.filter((x) => x.id !== r.id))}
            >
              <X size={14} strokeWidth={2} aria-hidden />
            </Button>
          </div>
          <input
            aria-label={`Input ${i + 1} description`}
            placeholder="What it is (optional)"
            className="inp"
            value={r.description}
            onChange={(e) => update(r.id, { description: e.target.value })}
          />
        </div>
      ))}
      <div>
        <Button size="sm" onClick={() => onChange([...rows, newInputRow()])}>
          <Plus size={14} strokeWidth={2.2} aria-hidden />
          Add input
        </Button>
      </div>
    </fieldset>
  );
}
